/** The views every participant screen reads, loaded once through the shared cache (screens §3.1: Loading, Error). */
import type { ContentCatalog, ErrorBody, MarketView, RoundReport, TeamState, WelcomeCard } from "@bsim/contracts";
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRoute, useRoutes } from "../api/context";

export type CohortData =
  | { readonly status: "loading"; readonly error: null }
  | { readonly status: "no-team"; readonly error: null }
  | { readonly status: "error"; readonly error: ErrorBody["error"] }
  | {
      readonly status: "ready"; readonly error: null;
      /** A refresh failed while the data below is still held: the page keeps showing it and says so (null when every read is fine). */
      readonly refreshError: ErrorBody["error"] | null;
      readonly userId: string;
      readonly team: TeamState; readonly catalog: ContentCatalog; readonly market: MarketView; readonly welcome: WelcomeCard;
      /** Released round reports, oldest first: the last `reportCount` rounds, or every released round. */
      readonly reports: readonly RoundReport[];
    };

/** `reportCount` limits how many of the latest released rounds load their report (the cockpit needs two, financials all, P1 none). */
export function useCohortData(cohortId: string, teamId: string | null, reportCount: number | "all"): CohortData {
  const me = useRoute("authMe", undefined, { staleTime: Infinity });
  const team = useRoute("teamState", teamId === null ? null : { teamId });
  const catalog = useRoute("contentCatalog", { cohortId }, { staleTime: Infinity });
  const market = useRoute("publicMarket", { cohortId });
  const welcome = useRoute("welcomeCard", { cohortId }, { staleTime: Infinity });
  // The catalog follows the round (a guided round lists only the decisions it opens), but is cached for good: when it disagrees with the draft about
  // what is open, it is from an earlier round, so it is fetched again, once per round.
  const queryClient = useQueryClient();
  const refetched = useRef<string | null>(null);
  const draft = team.data?.draft;
  const stale = draft !== null && draft !== undefined && catalog.data !== undefined && draft.editableDecisionIds.length !== catalog.data.decisions.length && refetched.current !== draft.roundId;
  useEffect(() => { if (stale && draft !== null && draft !== undefined) { refetched.current = draft.roundId; void queryClient.invalidateQueries({ queryKey: ["contentCatalog"] }); } }, [stale, draft, queryClient]);
  const released = team.data?.releasedRounds ?? [];
  const wanted = reportCount === "all" ? released : reportCount === 0 ? [] : released.slice(-reportCount);
  const reports = useRoutes("roundReport", wanted.map((r) => ({ teamId: teamId ?? "", roundId: r.roundId })));

  if (teamId === null) return { status: "no-team", error: null };
  const failedQuery = [me, team, catalog, market, welcome, ...reports].find((q) => q.error !== null);
  // A failed refresh of a read that still holds its data is not an error page: the data stays and `refreshError` says so.
  const failed = [me, team, catalog, market, welcome, ...reports].find((q) => q.error !== null && q.data === undefined);
  if (failed?.error) return { status: "error", error: failed.error.result.error };
  if (me.data === undefined || team.data === undefined || catalog.data === undefined || market.data === undefined || welcome.data === undefined || reports.some((r) => r.data === undefined)) {
    return { status: "loading", error: null };
  }
  return {
    status: "ready", error: null, refreshError: failedQuery?.error?.result.error ?? null, userId: me.data.principal.userId,
    team: team.data, catalog: catalog.data, market: market.data, welcome: welcome.data,
    reports: reports.flatMap((r) => (r.data === undefined ? [] : [r.data])),
  };
}
